import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageCover, PageBody, Segmented, StatusDot } from '@/components/Page';
import { documentoDoCredito, lerDinheiro, resultadoDaSimulacao } from '@/lib/credito';
import { formatBRL } from '@/lib/format';

// Simulador Cash Black (spec 2026-11-01 §4): o consultor vê, antes de montar o pedido, quanto do crédito entra, o
// desconto máximo que ainda pode dar e o preço com e sem crédito. A conta é do nova_campanha (a mesma do reservar);
// nada é reservado. O resultado é um recibo: tabela, menos o desconto já dado, menos o crédito, dá o preço com crédito
// (no nova_campanha, precoSemCredito = tabela - desconto e precoComCredito = precoSemCredito - credito).
const FORMAS = [
  { value: 'pix', label: 'Pix' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'financeira', label: 'Financeira' },
  { value: 'cartao', label: 'Cartão' },
];
const ORIGENS = [{ value: 'cliente', label: 'Buscar pelo CPF/CNPJ' }, { value: 'manual', label: 'Digitar o saldo' }];

const INVALIDO = 'Valor inválido: use 1.234,56';

const reais = (v) => (v === null || v === undefined ? '—' : formatBRL(v));

// Campo de R$: o símbolo fica dentro do campo e o erro embaixo (aria-invalid + aria-describedby).
const CampoDinheiro = ({ id, rotulo, valor, onValor, erro, ariaLabel }) => (
  <div className="space-y-2">
    {rotulo && <Label htmlFor={id}>{rotulo}</Label>}
    <div className="relative">
      <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
        R$
      </span>
      <Input id={id} inputMode="decimal" placeholder="0,00" value={valor} onChange={(e) => onValor(e.target.value)}
        aria-invalid={erro ? 'true' : undefined} aria-describedby={erro ? `${id}-erro` : undefined}
        aria-label={ariaLabel}
        className={`pl-10 tabular-nums ${erro ? 'border-red-500 focus-visible:ring-red-500' : ''}`} />
    </div>
    {erro && <p id={`${id}-erro`} className="text-xs text-red-700 dark:text-red-300">{erro}</p>}
  </div>
);

const Etapa = ({ titulo, children }) => (
  <fieldset className="space-y-3">
    <legend className="text-sm font-medium">{titulo}</legend>
    {children}
  </fieldset>
);

// Uma linha do recibo; `menos` põe o sinal de subtração antes do valor.
const LinhaRecibo = ({ rotulo, valor, menos = false }) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <dt className="text-sm text-gray-500">{rotulo}</dt>
    <dd className="text-sm font-medium tabular-nums">
      {menos && valor ? `− ${reais(valor)}` : reais(valor)}
    </dd>
  </div>
);

const Recibo = ({ r, desatualizado }) => (
  <div className={`transition-opacity duration-150 ${desatualizado ? 'opacity-50' : ''}`} aria-live="polite">
    <dl>
      <LinhaRecibo rotulo="Preço de tabela" valor={r?.tabela ?? null} />
      <LinhaRecibo rotulo="Desconto já dado" valor={r?.desconto ?? null} menos />
      <div className="border-b border-border">
        <LinhaRecibo rotulo="Cash Black usado" valor={r?.credito ?? null} menos />
      </div>
    </dl>
    <div className="pt-4">
      <p className="text-sm text-gray-500">Preço com crédito</p>
      <p className="mt-1 text-[36px] font-medium leading-none tracking-[-0.02em] tabular-nums">
        {reais(r?.precoComCredito ?? null)}
      </p>
      <p className="mt-2 text-sm text-gray-500 tabular-nums">
        Sem o crédito: {reais(r?.precoSemCredito ?? null)}
      </p>
    </div>
    <dl className="mt-5 border-t border-border pt-2">
      <LinhaRecibo rotulo="Ainda pode dar de desconto" valor={r?.descontoVendedorMaximo ?? null} />
      <LinhaRecibo rotulo="Saldo que sobra ao cliente" valor={r?.saldoRestante ?? null} />
    </dl>
  </div>
);

const SimuladorCashBlack = () => {
  const [forma, setForma] = useState('pix');
  const [tabela, setTabela] = useState('');
  const [desconto, setDesconto] = useState('');
  const [origem, setOrigem] = useState('cliente');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [saldo, setSaldo] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [desligado, setDesligado] = useState(false);

  const documento = documentoDoCredito(cpfCnpj);
  const precoTabela = lerDinheiro(tabela);
  const descontoNoPreco = lerDinheiro(desconto) ?? 0;
  const saldoDigitado = lerDinheiro(saldo);
  const erroTabela = Number.isNaN(precoTabela) ? INVALIDO : null;
  const erroDesconto = Number.isNaN(descontoNoPreco) ? INVALIDO : null;
  const erroSaldo = origem === 'manual' && Number.isNaN(saldoDigitado) ? INVALIDO : null;
  const erro = erroTabela || erroDesconto || erroSaldo ? 'Corrija os valores marcados.'
    : !(precoTabela > 0) ? 'Informe o preço de tabela.'
      : descontoNoPreco > precoTabela ? 'O desconto não pode passar do preço de tabela.'
        : origem === 'cliente' && !documento ? 'Informe um CPF ou CNPJ válido.'
          : origem === 'manual' && saldoDigitado === null ? 'Informe o saldo do cliente.' : null;

  // O que foi simulado: mudou algum campo depois, o recibo esmaece até simular de novo.
  const entrada = JSON.stringify([forma, precoTabela, descontoNoPreco, origem, documento, saldoDigitado]);
  const desatualizado = Boolean(resultado) && resultado.entrada !== entrada;

  const simular = async (e) => {
    e.preventDefault();
    if (erro) {
      toast.error(erro);
      return;
    }
    setCarregando(true);
    try {
      const res = await axios.post(`${API_URL}/credito-bcmed/simular`, {
        forma_pagamento: forma, preco_tabela: precoTabela, desconto: descontoNoPreco,
        ...(origem === 'cliente' ? { cpf_cnpj: documento } : { saldo: saldoDigitado }),
      });
      setDesligado(!res.data?.habilitado);
      const r = resultadoDaSimulacao(res.data);
      setResultado(r && { ...r, tabela: precoTabela, desconto: descontoNoPreco, entrada });
    } catch (err) {
      setResultado(null);
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Simulador indisponível agora');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div>
      <PageCover
        title="Simulador Cash Black"
        subtitle="Quanto do crédito entra na venda, pela forma de pagamento e o desconto já dado"
      />
      <PageBody>
        <Card className="overflow-hidden">
          <div className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <form onSubmit={simular} noValidate className="space-y-7 px-5 py-6 sm:px-7">
              <Etapa titulo="Forma de pagamento">
                <div className="overflow-x-auto">
                  <Segmented options={FORMAS} value={forma} onChange={setForma} label="Forma de pagamento" testid="sim-forma" />
                </div>
              </Etapa>

              <Etapa titulo="Preço">
                <div className="grid gap-4 sm:grid-cols-2">
                  <CampoDinheiro id="sim-tabela" rotulo="Preço de tabela" valor={tabela} onValor={setTabela}
                    erro={erroTabela} />
                  <CampoDinheiro id="sim-desconto" rotulo="Desconto já dado" valor={desconto}
                    onValor={setDesconto} erro={erroDesconto} />
                </div>
              </Etapa>

              <Etapa titulo="Saldo do cliente">
                <Segmented options={ORIGENS} value={origem} onChange={setOrigem} label="Origem do saldo" testid="sim-origem" />
                {origem === 'cliente' ? (
                  <Input aria-label="CPF ou CNPJ do cliente" inputMode="numeric" placeholder="CPF ou CNPJ" value={cpfCnpj}
                    onChange={(e) => setCpfCnpj(e.target.value)} className="tabular-nums" />
                ) : (
                  <CampoDinheiro id="sim-saldo" valor={saldo} onValor={setSaldo} erro={erroSaldo}
                    ariaLabel="Saldo de Cash Black" />
                )}
              </Etapa>

              <Button type="submit" disabled={carregando} className="w-full sm:w-auto sm:min-w-32">
                {carregando ? 'Simulando...' : resultado ? 'Simular de novo' : 'Simular'}
              </Button>
            </form>

            <aside aria-label="Resultado da simulação"
              className="border-t border-border bg-accent px-5 py-6 sm:px-7 lg:border-l lg:border-t-0">
              <p className="text-sm font-medium">Resultado</p>
              <div className="mt-3">
                {desligado ? (
                  <StatusDot tone="off">Crédito BCMED não configurado neste ambiente</StatusDot>
                ) : (
                  <>
                    <Recibo r={resultado} desatualizado={desatualizado} />
                    {!resultado && (
                      <p className="mt-4 text-sm text-gray-500">Preencha a venda ao lado e simule.</p>
                    )}
                    {desatualizado && (
                      <StatusDot tone="att" className="mt-4">Os dados mudaram. Simule de novo.</StatusDot>
                    )}
                    {resultado?.motivo && !desatualizado && (
                      <StatusDot tone="att" className="mt-4">{resultado.motivo}</StatusDot>
                    )}
                    {resultado?.regulamentoUrl && (
                      <a href={resultado.regulamentoUrl} target="_blank" rel="noopener noreferrer"
                        className="mt-4 block text-xs font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none dark:text-blue-400">
                        Regulamento do Cash Black
                      </a>
                    )}
                  </>
                )}
              </div>
            </aside>
          </div>
        </Card>
      </PageBody>
    </div>
  );
};

export default SimuladorCashBlack;
